import {
  EXERCISE_TYPE_NAMES,
  ExerciseList,
  ExerciseResponse,
  MUSCLE_GROUP_IDS,
  MUSCLE_GROUP_NAMES,
  OkResponse,
  searchExercises,
  type Exercise,
  type ExerciseType,
  type MuscleGroup,
} from '@squats/shared';
import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'wouter';
import { api, errorText } from '../api.ts';
import { useUser } from '../auth.tsx';
import { isPlanner } from '../calendar.tsx';
import { MuscleChips, MusclePicker } from '../components/Muscles.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useApi } from '../useApi.ts';

export function ExercisesPage() {
  const user = useUser();
  const { data, error, reload } = useApi(() => api('/api/exercises', ExerciseList), []);
  const [query, setQuery] = useState('');
  const [muscle, setMuscle] = useState<MuscleGroup | ''>('');
  const [adding, setAdding] = useState(false);

  const shown = useMemo(() => {
    const forMuscle = (data?.exercises ?? []).filter((e) => !muscle || e.primaryMuscles.includes(muscle) || e.secondaryMuscles.includes(muscle));
    return searchExercises(forMuscle, query);
  }, [data, query, muscle]);

  return (
    <div className="stack-lg">
      <div className="row">
        <h1 className="grow">Exercises</h1>
        {isPlanner(user) && (
          <button type="button" className="btn small primary" onClick={() => setAdding(true)}>
            Add exercise
          </button>
        )}
      </div>

      <div className="row wrap">
        <label className="field grow">
          <span>Search</span>
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. squat" />
        </label>
        <label className="field">
          <span>Muscle group</span>
          <select value={muscle} onChange={(e) => setMuscle(e.target.value as MuscleGroup | '')}>
            <option value="">All</option>
            {MUSCLE_GROUP_IDS.map((g) => (
              <option key={g} value={g}>
                {MUSCLE_GROUP_NAMES[g]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="error">{error}</p>}
      {data && (
        <section className="card" aria-label="Exercise list">
          <p className="muted small">
            {shown.length} of {data.exercises.length}
          </p>
          <ul className="list">
            {shown.map((e) => (
              <li key={e.id}>
                <Link href={`/exercises/${e.id}`} className="list-row link-row">
                  <div className="grow stack-sm">
                    <span className="row-title">
                      {e.name} {!e.builtIn && <span className="pill">Added</span>}
                    </span>
                    <span className="muted small">{EXERCISE_TYPE_NAMES[e.type]}</span>
                    <span>
                      <MuscleChips groups={e.primaryMuscles} /> <MuscleChips groups={e.secondaryMuscles} muted />
                    </span>
                  </div>
                  <span className="chev" aria-hidden="true">
                    ›
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Sheet open={adding} onClose={() => setAdding(false)} title="Add exercise">
        {adding && (
          <ExerciseForm
            builtIns={(data?.exercises ?? []).filter((e) => e.builtIn)}
            onDone={() => {
              setAdding(false);
              reload();
            }}
          />
        )}
      </Sheet>
    </div>
  );
}

/** Add or edit a custom exercise. `builtIns` feeds the "Moves like" picker. */
export function ExerciseForm({ initial, builtIns, onDone }: { initial?: Exercise; builtIns: Exercise[]; onDone: () => void }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<ExerciseType>(initial?.type ?? 'weight_reps');
  const [primary, setPrimary] = useState<MuscleGroup[]>(initial?.primaryMuscles ?? []);
  const [secondary, setSecondary] = useState<MuscleGroup[]>(initial?.secondaryMuscles ?? []);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [looksLike, setLooksLike] = useState(initial?.looksLike ?? '');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const body = {
        name,
        type,
        primaryMuscles: primary,
        secondaryMuscles: secondary.filter((g) => !primary.includes(g)),
        notes,
        looksLike: looksLike || null,
      };
      if (initial) await api(`/api/exercises/${initial.id}`, ExerciseResponse, { method: 'PATCH', body });
      else await api('/api/exercises', ExerciseResponse, { body });
      onDone();
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function remove() {
    if (!initial || !confirm(`Remove “${initial.name}” from the library?`)) return;
    try {
      await api(`/api/exercises/${initial.id}`, OkResponse, { method: 'DELETE' });
      onDone();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <form className="stack" onSubmit={submit}>
      <label className="field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required autoFocus />
      </label>
      <label className="field">
        <span>What you record</span>
        <select value={type} onChange={(e) => setType(e.target.value as ExerciseType)}>
          {Object.entries(EXERCISE_TYPE_NAMES).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <MusclePicker label="Main muscle groups" value={primary} onChange={setPrimary} />
      <MusclePicker label="Also works (optional)" value={secondary} onChange={setSecondary} exclude={primary} />
      <label className="field">
        <span>Moves like (borrows its animation)</span>
        <select value={looksLike} onChange={(e) => setLooksLike(e.target.value)}>
          <option value="">No animation</option>
          {builtIns.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>How-to notes (setup, steps, safety tips)</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} rows={5} />
      </label>
      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="submit" className="btn primary" disabled={!name.trim() || primary.length === 0}>
          {initial ? 'Save' : 'Add exercise'}
        </button>
        {initial && (
          <button type="button" className="btn danger" onClick={() => void remove()}>
            Remove
          </button>
        )}
      </div>
    </form>
  );
}
